import {
  type DraftPickStarsResponse,
  type DraftPickStateResponsePagedData,
  type DraftPickYearResponsePagedData,
  type OrganizationDraftRankingSummaryResponse,
  type OrganizationDraftYearsResponsePagedData,
  type OrgDraftByStarsSummaryResponse,
  rdbV1FiltersYearsList,
  type TeamFilter,
} from '@on3/api';
import type { IDraftHistoryPageProps } from '@on3/ui-lib/api/schema/custom-contracts';
import withAuthentication from '@on3/ui-lib/hocs/withAuthentication';
import withValidUrl from '@on3/ui-lib/hocs/withValidUrl';
import { proxyApi } from '@on3/ui-lib/utils/api';
import { DraftHistoryList } from '@public/components/Db/Draft/DraftHistoryList';
import { DatabaseLayout } from '@public/components/Layouts/DatabaseLayout';
import { PageWrapper } from '@public/components/Layouts/PageWrapper';
import type { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';

const NFLDraftHistoryPage = (props: IDraftHistoryPageProps) => {
  const router = useRouter();
  const { year, 'team-key': teamKey, 'year-span': span = 1 } = router.query;
  const yearSpan = span && +span !== 1 ? `- Last ${span} Years` : '';
  const descriptionYearSpan = span && year && `${+year - +span + 1}-` + year;
  const basePath = router.asPath.split('?')[0];

  if (!teamKey && typeof window !== 'undefined') {
    router.push(`${basePath}?team-key=1867`);
  }

  //empty array to push all teams to
  const teamsArray: TeamFilter[] = [];

  //push all teams into single array to use find method
  props.filters.teams.map((conf) => {
    conf?.teams?.map((team) => teamsArray.push(team));
  });
  //current team object
  const currentTeam =
    teamKey && teamsArray?.find((team) => team?.orgKey === +teamKey);
  const teamName = currentTeam ? currentTeam?.orgName : '';

  const description = `${teamName}'s ${
    +span > 1 ? descriptionYearSpan : year
  } NFL draft history, including the number of draft picks per year, draft blue chip share, and draft talent ratio.`;

  const title = `${year} ${teamName} NFL Draft History By Year ${yearSpan}`;
  const noIndex = props?.draftList?.pagination?.count === 0;

  return (
    <PageWrapper
      {...props}
      description={description}
      noIndex={noIndex}
      title={title}
    >
      <DatabaseLayout>
        <DraftHistoryList
          key={`NFL-${teamKey}-${year}-${span}`}
          {...props}
          currentTeam={currentTeam ? currentTeam : null}
          title={title}
        />
      </DatabaseLayout>
    </PageWrapper>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const siteData = await withValidUrl.isValid(ctx);

  if (!siteData) {
    return {
      notFound: true,
    };
  }

  const userData = await withAuthentication.isAuth(ctx);
  const { year, 'team-key': teamKey = 1867, 'year-span': span = 1 } = ctx.query;

  const params = {
    sportKey: 1,
    organizaionKey: teamKey || 1867,
    year,
    yearSpan: span,
  };

  if (!year) {
    return {
      notFound: true,
    };
  }

  try {
    const [
      draftList,
      statePicks,
      starPicks,
      yearPicks,
      starSummary,
      pickSummary,
      teams,
      years,
    ] = await Promise.allSettled([
      //Main List draft by year
      proxyApi
        .get<OrganizationDraftYearsResponsePagedData>(
          `/public/rdb/v1/organizations/${params.organizaionKey}/draft-class-by-year`,
          {
            params,
          },
        )
        .then((r) => r.data),
      //Draft by State module
      proxyApi
        .get<DraftPickStateResponsePagedData>(
          `/public/rdb/v1/organizations/${params.organizaionKey}/draft-class-by-state`,
          {
            params,
          },
        )
        .then((r) => r.data),
      //Draft by Stars module
      proxyApi
        .get<DraftPickStarsResponse>(
          `/public/rdb/v1/organizations/${params.organizaionKey}/draft-count-by-stars`,
          {
            params,
          },
        )
        .then((r) => r.data),
      //Draft count by year
      proxyApi
        .get<DraftPickYearResponsePagedData>(
          `/public/rdb/v1/organizations/${params.organizaionKey}/draft-count-by-year`,
          {
            params,
          },
        )
        .then((r) => r.data),
      //Star Summary aka Pie Chart
      proxyApi
        .get<OrgDraftByStarsSummaryResponse>(
          `/public/rdb/v1/organizations/${params.organizaionKey}/drafts-by-stars-summary`,
          {
            params,
          },
        )
        .then((r) => r.data),
      //Pick Summary
      proxyApi
        .get<OrganizationDraftRankingSummaryResponse>(
          `/public/rdb/v1/organizations/${params.organizaionKey}/draft-ranking-summary`,
          {
            params,
          },
        )
        .then((r) => r.data),
      //Teams Filter
      proxyApi
        .get(
          `/public/rdb/v1/filters/teams?groupBy=true&sportKey=1&year=${year}`,
        )
        .then((r) => r.data),
      rdbV1FiltersYearsList(proxyApi),
    ]);

    const data = {
      pageType: 'database',
      siteData: siteData || [],
      userData: userData || {},
      draftList: draftList.status === 'fulfilled' ? draftList.value : [],
      modules: {
        statePicks: statePicks.status === 'fulfilled' ? statePicks.value : [],
        starPicks: starPicks.status === 'fulfilled' ? starPicks.value : [],
        yearPicks: yearPicks.status === 'fulfilled' ? yearPicks.value : [],
        starSummary:
          starSummary.status === 'fulfilled' ? starSummary.value : [],
        pickSummary:
          pickSummary.status === 'fulfilled' ? pickSummary.value : [],
      },
      filters: {
        teams: teams.status === 'fulfilled' ? teams.value : [],
        years: years.status === 'fulfilled' ? years.value : [],
      },
    };

    return { props: { ...data } };
  } catch (e) {
    return { props: [] };
  }
};

export default withValidUrl(NFLDraftHistoryPage);
